/**
 * StatusWriter — Lead-only modal for composing or editing a status update.
 *
 * DEV-1079, Chunk A. The novel piece of the redesign. Bound to the
 * Summary KPI shape's click for the Lead (create mode), and to the
 * "edit this status" pencil on StatusSection (edit mode).
 *
 * Composition (top to bottom):
 *   - Reporting date picker (StatusCalendarPicker write-mode pill).
 *     In edit mode the date is read-only (slot identifier).
 *   - "What's happening?" TipTap rich-text editor (minimal toolbar)
 *   - Photo strip (StatusPhotoStrip write-mode, soft cap 6)
 *   - Schedule progress: planned % + actual % inputs with live chip
 *     showing the predicted color band
 *   - Mission goals: per-goal 5-state segmented controls
 *
 * mode="create":
 *   Create a brand-new status. If `previousStatus` is set, quick-update
 *   pre-fill: narrative + planned/actual + per-goal assessments AND
 *   photos are seeded from it (Skip's 2026-05-22 decision — photos
 *   carry forward like every other status field). Per-status photo
 *   arrays are independent: removing a photo from this new status
 *   doesn't touch the previous status's array. The Media tab is a
 *   union projection across all statuses, deduped by storage_key, so
 *   a photo still referenced by an older status keeps appearing in
 *   Media even if removed from this one. Edge case: a photo uploaded
 *   only to this status, then removed before save (or after) disappears
 *   from Media — accepted per model (a) (union semantics).
 *   A banner at top tells the Lead "pre-filled from [date]" with a
 *   "Start fresh" link that clears all fields including photos. On
 *   Save → createMiniProjectStatus.
 *
 * mode="edit":
 *   Amend an existing status (`editingStatus`). All fields are preloaded
 *   from that status including its photos. Reporting date is locked —
 *   it's the slot identifier. No pre-fill banner. On Save →
 *   updateMiniProjectStatus on that status's GUID.
 *
 * Strings localized via `pages:miniProject.status.writer.*` (Chunk D
 * Phase 1 sweep, 2026-05-22).
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import StatusCalendarPicker from "../StatusCalendarPicker/StatusCalendarPicker";
import StatusPhotoStrip, { type StatusPhotoItem } from "../StatusPhotoStrip/StatusPhotoStrip";
import {
  createMiniProjectStatus,
  updateMiniProjectStatus,
  type MiniProjectStatus,
  type MiniProjectAssessment,
  type MiniProjectBudgetAssessment,
} from "../../api/miniProjectStatuses";
import type { MiniProjectGoal } from "../../api/miniProjects";
import type { MiniProjectGoalView } from "../../Data/MiniProjectConfig/miniProjectGoalView";
import GoalDataEntryTray from "../MiniProjectGoalEdit/GoalDataEntryTray";
import {
  classifyScheduleGap,
  computeScheduleThresholds,
  DEFAULT_SCHEDULE_THRESHOLDS,
} from "../../Data/MiniProjectConfig/scheduleThresholds";
import styles from "./StatusWriter.module.css";

/* ── Types ─────────────────────────────────────────────────────── */

interface Props {
  open: boolean;
  onClose: () => void;
  miniProjectGuid: string;
  /** "create": brand-new status (optionally pre-filled from previousStatus).
   *  "edit": amend editingStatus. Date locked, photos preloaded. */
  mode?: "create" | "edit";
  /** Required in edit mode. Ignored in create mode. */
  editingStatus?: MiniProjectStatus | null;
  /** Goals on the project at the time the writer opens. Caller
   *  pre-fetches; writer renders per-goal pickers from this list. */
  goals: MiniProjectGoal[];
  /** Most recent status, if any. When set in create mode, the writer
   *  pre-fills narrative / planned / actual / per-goal picks from it.
   *  Photos always start empty regardless. Ignored in edit mode. */
  previousStatus: MiniProjectStatus | null;
  /** YYYY-MM-DD dates already filed (calendar picker disables those). */
  filedDates: string[];
  /** Project duration in days, for auto-scaling schedule thresholds.
   *  Pass null when start/end dates aren't set — falls back to the
   *  static 5/15pp defaults. */
  durationDays?: number | null;
  /** Lead-set planned budget amount from MiniProject settings (migration
   *  190). When set together with recordedSpend, the Budget Progress
   *  auto-suggest computes a recommended assessment. When null, the
   *  Budget Progress section still renders but only as 4 manual buttons. */
  plannedBudgetAmount?: number | null;
  /** ISO 4217 code paired with plannedBudgetAmount. Display only —
   *  used to label the auto-suggest chip ("Suggested: Over Budget — USD 1,200 of USD 1,000"). */
  plannedBudgetCurrency?: string | null;
  /** Sum of BOM items' actualTotal at writer-open time. Drives the
   *  spend % used by the auto-suggest. Zero / undefined means no
   *  procurement has been recorded yet. */
  recordedSpend?: number;
  /** Called after a successful create/update. Caller refetches. */
  onSaved: () => void;
  /**
   * Optional. Called after the Lead saves new actual numeric data via
   * the in-modal GoalDataEntryTray (2026-05-27 — Skip's request: while
   * picking assessment chips, the Lead can also update the underlying
   * current value for any goal). The parent should refetch goal data
   * but MUST NOT close the status modal — this fires on goal-data
   * mutation only, not on status save. When omitted, the data-entry
   * tray still works; the inline display in the modal just stays
   * stale until the next mount.
   */
  onGoalDataChanged?: () => void;
}

/** Adapt a wire-level MiniProjectGoal to the MiniProjectGoalView shape
 *  GoalDataEntryTray expects. The tray only reads {goalGuid,
 *  miniProjectGuid, nameKey, primitive, unitKey, targetValue,
 *  currentValue, currencyCode} from the goal; the rest of the View
 *  fields (kpiConfigId, derivedChecklist, lastUpdatedAt, …) are
 *  unused at runtime but must satisfy the type. kpiConfigId == goalGuid
 *  matches the view's own contract ("same value as goalGuid; exposed
 *  separately so the card knows…"). 2026-05-27. */
function adaptGoalToView(g: MiniProjectGoal): MiniProjectGoalView {
  return {
    goalGuid: g.goalGuid,
    miniProjectGuid: g.miniProjectGuid,
    kpiConfigId: g.goalGuid,
    nameKey: g.nameKey,
    primitive: g.primitive,
    unitKey: g.unitKey ?? null,
    targetValue: g.targetValue ?? null,
    currentValue: g.currentValue,
    required: g.required,
    checklistItems: g.checklistItems ?? null,
    derivedChecklist: null,
    position: g.position,
    currencyCode: g.currencyCode ?? null,
    lastUpdatedAt: g.lastUpdatedAt,
    createdAt: g.createdAt,
    updatedAt: g.updatedAt,
  } as MiniProjectGoalView;
}

/** Four-state ladder (migration 190, Skip 2026-05-28). */
const BUDGET_STATES: { value: MiniProjectBudgetAssessment; labelKey: string; color: "success" | "warning" | "error" }[] = [
  { value: "under_budget",   labelKey: "miniProject.status.writer.budget.underBudget",   color: "success" },
  { value: "on_track",       labelKey: "miniProject.status.writer.budget.onTrack",       color: "success" },
  { value: "trending_over",  labelKey: "miniProject.status.writer.budget.trendingOver",  color: "warning" },
  { value: "over_budget",    labelKey: "miniProject.status.writer.budget.overBudget",    color: "error" },
];

/** Compute a suggested budget assessment from spend math. Returns null
 *  when there's no plan or no spend — caller hides the suggestion chip
 *  in that case. Thresholds chosen 2026-05-28 with Skip:
 *    < 90%  → under_budget
 *    < 100% → on_track
 *    ≤ 110% → trending_over
 *    > 110% → over_budget
 *  Lead can always override. */
function suggestBudgetAssessment(
  plannedAmount: number | null | undefined,
  spent: number | undefined,
): MiniProjectBudgetAssessment | null {
  if (!plannedAmount || plannedAmount <= 0) return null;
  if (!spent || spent <= 0) return null;
  const pct = (spent / plannedAmount) * 100;
  if (pct < 90) return "under_budget";
  if (pct < 100) return "on_track";
  if (pct <= 110) return "trending_over";
  return "over_budget";
}

// Assessment state list. Labels resolved at render time via a t() lookup
// so they localize correctly; the `value` is the wire-level enum.
const ASSESSMENT_STATES: { value: MiniProjectAssessment; labelKey: string }[] = [
  { value: "ahead", labelKey: "miniProject.status.writer.assessment.ahead" },
  { value: "on_track", labelKey: "miniProject.status.writer.assessment.onTrack" },
  { value: "behind", labelKey: "miniProject.status.writer.assessment.behind" },
  { value: "blocked", labelKey: "miniProject.status.writer.assessment.blocked" },
  { value: "done", labelKey: "miniProject.status.writer.assessment.done" },
];

/* ── Helpers ───────────────────────────────────────────────────── */

function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function formatLongDate(iso: string): string {
  try {
    const [y, m, d] = iso.split("-").map((s) => parseInt(s, 10));
    return new Date(y, (m || 1) - 1, d || 1).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

/* Schedule classifier is now imported from
 * src/Data/MiniProjectConfig/scheduleThresholds.ts — see that module
 * for the auto-scaling rule (DEV-1080, Chunk B). */

/* ── Component ─────────────────────────────────────────────────── */

const StatusWriter: React.FC<Props> = ({
  open,
  onClose,
  miniProjectGuid,
  mode = "create",
  editingStatus = null,
  goals,
  previousStatus,
  filedDates,
  durationDays = null,
  plannedBudgetAmount = null,
  plannedBudgetCurrency = null,
  recordedSpend = 0,
  onSaved,
  onGoalDataChanged,
}) => {
  const { t } = useAppTranslation("pages");

  // 2026-05-27 — in-modal goal data entry. Lead clicks the database
  // icon next to a goal in the assessment list, the GoalDataEntryTray
  // opens (portal-rendered, slides over the status modal), Lead
  // updates the actual numeric value, save fires onGoalDataChanged so
  // the parent refetches goals — the new currentValue then shows in
  // the inline meta row beside the assessment chips. Tray close does
  // NOT close the status modal; the Lead returns to picking chips.
  const [dataEntryGoalGuid, setDataEntryGoalGuid] = useState<string | null>(null);
  const dataEntryGoal = dataEntryGoalGuid
    ? goals.find((g) => g.goalGuid === dataEntryGoalGuid) ?? null
    : null;
  const isEdit = mode === "edit" && !!editingStatus;
  // Auto-scale thresholds per project duration (DEV-1080, Chunk B).
  // Short projects get wider tolerances automatically; the floor is the
  // static 5/15pp default.
  const thresholds = useMemo(
    () => computeScheduleThresholds(durationDays),
    [durationDays],
  );
  // ── Form state ──
  // Initialized lazily so the writer always opens "fresh" (or pre-filled
  // if previousStatus is set). Resetting on open transition handled
  // below via prevOpenRef.
  const [reportingDate, setReportingDate] = useState<string>(todayIso());
  const [plannedPct, setPlannedPct] = useState<string>("");
  const [actualPct, setActualPct] = useState<string>("");
  const [photos, setPhotos] = useState<StatusPhotoItem[]>([]);
  const [assessments, setAssessments] = useState<Record<string, MiniProjectAssessment>>({});
  // Budget assessment (migration 190). Null = not yet picked. The
  // Save handler ships null through to the API, which treats it as
  // "not assessed" (Budget KPI dot stays grey).
  const [budgetAssessment, setBudgetAssessment] = useState<MiniProjectBudgetAssessment | null>(null);
  // DEV-1296 — Lead-only, write/edit-time Journal/feed posting.
  const [postToJournal, setPostToJournal] = useState(false);
  const [journalHeadline, setJournalHeadline] = useState("");
  const [postToFeed, setPostToFeed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Has the Lead clicked "Start fresh"? Suppresses the pre-fill banner
   *  and forces blank state until next modal open. */
  const [startedFresh, setStartedFresh] = useState(false);

  // ── TipTap editor ──
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: false, // no headings in status narrative (per spec)
      }),
      Link.configure({ openOnClick: false }),
      Placeholder.configure({
        placeholder: t("miniProject.status.writer.placeholder"),
      }),
    ],
    content: "",
  });

  // ── On open: reset form, preload depending on mode ──
  const prevOpenRef = React.useRef(false);
  useEffect(() => {
    if (!open) {
      prevOpenRef.current = false;
      return;
    }
    if (prevOpenRef.current) return;
    prevOpenRef.current = true;

    setError(null);
    setSaving(false);
    setStartedFresh(false);

    if (isEdit && editingStatus) {
      // Edit mode: preload everything (including photos) from the
      // existing status. Reporting date is locked.
      setReportingDate(editingStatus.reportingDate);
      setPlannedPct(String(editingStatus.plannedPct));
      setActualPct(String(editingStatus.actualPct));
      setPhotos(
        editingStatus.photos.map((p) => ({
          storageKey: p.storageKey,
          caption: p.caption ?? null,
          position: p.position,
        })),
      );
      const editAssess: Record<string, MiniProjectAssessment> = {};
      for (const a of editingStatus.goalAssessments) {
        editAssess[a.goalGuid] = a.assessment;
      }
      setAssessments(editAssess);
      setBudgetAssessment(editingStatus.budgetAssessment ?? null);
      // DEV-1296 — restore the Journal/feed state so re-saving doesn't un-post it.
      setPostToJournal(editingStatus.postedToJournal ?? false);
      setJournalHeadline(editingStatus.journalHeadline ?? "");
      setPostToFeed(editingStatus.postedToFeed ?? false);
      editor?.commands.setContent(editingStatus.narrativeHtml || "");
      return;
    }

    // Create mode.
    setReportingDate(todayIso());
    if (previousStatus) {
      // Quick-update / pre-fill from the most recent status. Photos
      // carry forward as references (storage_keys) — per Skip's
      // 2026-05-22 call. Per-status arrays are independent so editing
      // one doesn't affect the other. See header comment for the full
      // lifecycle rationale.
      setPlannedPct(String(previousStatus.plannedPct));
      setActualPct(String(previousStatus.actualPct));
      setPhotos(
        previousStatus.photos.map((p) => ({
          storageKey: p.storageKey,
          caption: p.caption ?? null,
          position: p.position,
        })),
      );
      const prevAssess: Record<string, MiniProjectAssessment> = {};
      for (const a of previousStatus.goalAssessments) {
        prevAssess[a.goalGuid] = a.assessment;
      }
      setAssessments(prevAssess);
      setBudgetAssessment(previousStatus.budgetAssessment ?? null);
      editor?.commands.setContent(previousStatus.narrativeHtml || "");
    } else {
      // Blank slate — no previous status to carry forward from.
      setPlannedPct("");
      setActualPct("");
      setPhotos([]);
      setAssessments({});
      setBudgetAssessment(null);
      editor?.commands.clearContent();
    }
  }, [open, isEdit, editingStatus, previousStatus, editor]);

  // ── Handlers ──
  const handleStartFresh = useCallback(() => {
    setStartedFresh(true);
    setPlannedPct("");
    setActualPct("");
    setAssessments({});
    setBudgetAssessment(null);
    setPhotos([]);
    editor?.commands.clearContent();
  }, [editor]);

  const handleAssessmentPick = useCallback(
    (goalGuid: string, value: MiniProjectAssessment) => {
      setAssessments((m) => ({ ...m, [goalGuid]: value }));
    },
    [],
  );

  const handleSave = useCallback(async () => {
    setError(null);

    // Client-side validation that mirrors server-side rules. The server
    // is the source of truth for rejection messages; this is a UX
    // optimization to avoid round-trips.
    const narrative = (editor?.getHTML() || "").trim();
    const narrativeText = (editor?.getText() || "").trim();
    if (!narrativeText) {
      setError(t("miniProject.status.writer.error.narrativeEmpty"));
      return;
    }
    const plannedNum = Number(plannedPct);
    const actualNum = Number(actualPct);
    if (!Number.isFinite(plannedNum) || plannedNum < 0 || plannedNum > 100) {
      setError(t("miniProject.status.writer.error.plannedRange"));
      return;
    }
    if (!Number.isFinite(actualNum) || actualNum < 0 || actualNum > 100) {
      setError(t("miniProject.status.writer.error.actualRange"));
      return;
    }
    const missing = goals.filter((g) => !assessments[g.goalGuid]);
    if (missing.length > 0) {
      setError(
        t("miniProject.status.writer.error.missingGoals", {
          names: missing.map((g) => g.nameKey).join(", "),
        }),
      );
      return;
    }

    setSaving(true);
    try {
      const photosBody = photos.map((p, i) => ({
        storageKey: p.storageKey,
        caption: p.caption ?? null,
        position: i,
      }));
      const goalAssessmentsBody = goals.map((g) => ({
        goalGuid: g.goalGuid,
        assessment: assessments[g.goalGuid],
      }));

      const journalFields = {
        postToJournal,
        journalHeadline: postToJournal ? journalHeadline.trim() || null : null,
        postToFeed: postToJournal ? postToFeed : false,
      };
      if (isEdit && editingStatus) {
        await updateMiniProjectStatus(miniProjectGuid, editingStatus.statusGuid, {
          narrativeHtml: narrative,
          plannedPct: plannedNum,
          actualPct: actualNum,
          photos: photosBody,
          goalAssessments: goalAssessmentsBody,
          budgetAssessment,
          ...journalFields,
        });
      } else {
        await createMiniProjectStatus(miniProjectGuid, {
          reportingDate,
          narrativeHtml: narrative,
          plannedPct: plannedNum,
          actualPct: actualNum,
          photos: photosBody,
          goalAssessments: goalAssessmentsBody,
          budgetAssessment,
          ...journalFields,
        });
      }
      // Fire the persist event so the on-page StatusSection (and any
      // other listening surfaces) refresh from the server. Mirrors the
      // Geo Community Leaders _persisted pattern.
      window.dispatchEvent(
        new CustomEvent("l4e_miniproject_status_persisted", {
          detail: { miniProjectGuid },
        }),
      );
      onSaved();
      onClose();
    } catch (err: any) {
      setError(err?.body?.error || err?.message || t("miniProject.status.writer.error.saveFailed"));
    } finally {
      setSaving(false);
    }
  }, [
    editor,
    plannedPct,
    actualPct,
    goals,
    assessments,
    budgetAssessment,
    postToJournal,
    journalHeadline,
    postToFeed,
    miniProjectGuid,
    reportingDate,
    photos,
    onSaved,
    onClose,
    isEdit,
    editingStatus,
    t,
  ]);

  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  // ── Computed display values ──
  const gapChip = useMemo(() => {
    const p = Number(plannedPct);
    const a = Number(actualPct);
    if (!Number.isFinite(p) || !Number.isFinite(a)) return null;
    return classifyScheduleGap(p, a, thresholds);
  }, [plannedPct, actualPct, thresholds]);

  // Migration 190 — Budget Progress auto-suggest. Computed from current
  // plannedBudgetAmount + recordedSpend props at writer-open time. Null
  // when there's no plan or no spend; UI hides the chip in that case.
  const suggestedBudget = useMemo(
    () => suggestBudgetAssessment(plannedBudgetAmount, recordedSpend),
    [plannedBudgetAmount, recordedSpend],
  );
  const spendPct = useMemo(() => {
    if (!plannedBudgetAmount || plannedBudgetAmount <= 0) return null;
    if (!recordedSpend || recordedSpend <= 0) return 0;
    return (recordedSpend / plannedBudgetAmount) * 100;
  }, [plannedBudgetAmount, recordedSpend]);

  // No pre-fill banner in edit mode — edit always loads from the
  // status being edited, never from a separate "previous" record.
  const showPrefillBanner = !isEdit && !!previousStatus && !startedFresh;

  if (!open) return null;

  const modal = (
    <div
      className={styles.backdrop}
      onClick={(e) => {
        if (e.target === e.currentTarget) handleCancel();
      }}
    >
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-label={isEdit
          ? t("miniProject.status.writer.dialog.edit")
          : t("miniProject.status.writer.dialog.create")}
      >
        {/* ── Header ── */}
        <header className={styles.header}>
          <span className={styles.title}>
            {isEdit
              ? t("miniProject.status.writer.title.edit", { date: formatLongDate(editingStatus!.reportingDate) })
              : t("miniProject.status.writer.title.create")}
          </span>
          <button
            type="button"
            className={styles.closeBtn}
            onClick={handleCancel}
            aria-label={t("miniProject.status.writer.close")}
          >
            ×
          </button>
        </header>

        {/* ── Body ── */}
        <div className={styles.body}>
          {showPrefillBanner && (
            <div className={styles.prefillBanner}>
              <span>
                {t("miniProject.status.writer.prefill.banner", { date: formatLongDate(previousStatus!.reportingDate) })}
              </span>
              <button
                type="button"
                className={styles.prefillBtn}
                onClick={handleStartFresh}
              >
                {t("miniProject.status.writer.prefill.startFresh")}
              </button>
            </div>
          )}

          {/* Reporting date — locked in edit mode (slot identifier). */}
          <div className={styles.fieldRow}>
            <label className={styles.fieldLabel}>{t("miniProject.status.writer.field.reportingDate")}</label>
            {isEdit ? (
              <span className={styles.fieldHint}>
                {t("miniProject.status.writer.field.dateLocked", { date: formatLongDate(reportingDate) })}
              </span>
            ) : (
              <StatusCalendarPicker
                value={reportingDate}
                filedDates={filedDates}
                mode="write"
                onChange={setReportingDate}
              />
            )}
          </div>

          {/* Narrative */}
          <div className={styles.fieldBlock}>
            <label className={styles.fieldLabel}>{t("miniProject.status.writer.field.narrative")}</label>
            <div className={styles.editorToolbar}>
              <ToolbarBtn
                editor={editor}
                ariaLabel={t("miniProject.status.writer.toolbar.bold")}
                onClick={() => editor?.chain().focus().toggleBold().run()}
                active={!!editor?.isActive("bold")}
              >
                B
              </ToolbarBtn>
              <ToolbarBtn
                editor={editor}
                ariaLabel={t("miniProject.status.writer.toolbar.italic")}
                onClick={() => editor?.chain().focus().toggleItalic().run()}
                active={!!editor?.isActive("italic")}
              >
                <i>I</i>
              </ToolbarBtn>
              <ToolbarBtn
                editor={editor}
                ariaLabel={t("miniProject.status.writer.toolbar.bulletList")}
                onClick={() => editor?.chain().focus().toggleBulletList().run()}
                active={!!editor?.isActive("bulletList")}
              >
                •
              </ToolbarBtn>
              <ToolbarBtn
                editor={editor}
                ariaLabel={t("miniProject.status.writer.toolbar.orderedList")}
                onClick={() => editor?.chain().focus().toggleOrderedList().run()}
                active={!!editor?.isActive("orderedList")}
              >
                1.
              </ToolbarBtn>
              <ToolbarBtn
                editor={editor}
                ariaLabel={t("miniProject.status.writer.toolbar.quote")}
                onClick={() => editor?.chain().focus().toggleBlockquote().run()}
                active={!!editor?.isActive("blockquote")}
              >
                "
              </ToolbarBtn>
              <ToolbarBtn
                editor={editor}
                ariaLabel={t("miniProject.status.writer.toolbar.link")}
                onClick={() => {
                  const url = window.prompt(t("miniProject.status.writer.toolbar.linkPrompt"));
                  if (url) editor?.chain().focus().setLink({ href: url }).run();
                }}
                active={!!editor?.isActive("link")}
              >
                🔗
              </ToolbarBtn>
            </div>
            <div className={styles.editorWrap}>
              <EditorContent editor={editor} />
            </div>
          </div>

          {/* Photos */}
          <div className={styles.fieldBlock}>
            <div className={styles.fieldLabelRow}>
              <label className={styles.fieldLabel}>{t("miniProject.status.writer.field.photos")}</label>
              <span className={styles.fieldHint}>{t("miniProject.status.writer.field.photosHint", { count: photos.length })}</span>
            </div>
            <StatusPhotoStrip mode="write" photos={photos} onChange={setPhotos} maxPhotos={6} />
          </div>

          {/* Schedule progress */}
          <div className={styles.fieldBlock}>
            <label className={styles.fieldLabel}>{t("miniProject.status.writer.field.scheduleProgress")}</label>
            <div className={styles.scheduleRow}>
              <div className={styles.pctField}>
                <span className={styles.pctLabel}>{t("miniProject.status.writer.field.plannedPct")}</span>
                <input
                  type="number"
                  min={0}
                  max={100}
                  step="any"
                  className={styles.pctInput}
                  value={plannedPct}
                  onChange={(e) => setPlannedPct(e.target.value)}
                />
              </div>
              <div className={styles.pctField}>
                <span className={styles.pctLabel}>{t("miniProject.status.writer.field.actualPct")}</span>
                <input
                  type="number"
                  min={0}
                  max={100}
                  step="any"
                  className={styles.pctInput}
                  value={actualPct}
                  onChange={(e) => setActualPct(e.target.value)}
                />
              </div>
              {gapChip && (
                <div className={`${styles.gapChip} ${styles[`gapChip_${gapChip.color}`]}`}>
                  <span className={styles.gapDot} aria-hidden="true" />
                  <span>{gapChip.label}</span>
                </div>
              )}
            </div>
            <div className={styles.thresholdLegend}>
              <span style={{ fontWeight: 500, color: "var(--text-primary)" }}>
                {t("miniProject.status.writer.threshold.label")}
              </span>{" "}
              {t("miniProject.status.writer.threshold.green", { pts: thresholds.greenMaxPct.toFixed(0) })}{" "}
              <span style={{ color: "var(--color-success)" }}>
                {t("miniProject.status.writer.threshold.greenColor")}
              </span> ·{" "}
              {t("miniProject.status.writer.threshold.yellow", {
                lo: thresholds.greenMaxPct.toFixed(0),
                hi: thresholds.yellowMaxPct.toFixed(0),
              })}{" "}
              <span style={{ color: "var(--color-warning)" }}>
                {t("miniProject.status.writer.threshold.yellowColor")}
              </span> ·{" "}
              {t("miniProject.status.writer.threshold.red", { pts: thresholds.yellowMaxPct.toFixed(0) })}{" "}
              <span style={{ color: "var(--color-error)" }}>
                {t("miniProject.status.writer.threshold.redColor")}
              </span>
              {durationDays !== null && (thresholds.greenMaxPct > DEFAULT_SCHEDULE_THRESHOLDS.greenMaxPct
                || thresholds.yellowMaxPct > DEFAULT_SCHEDULE_THRESHOLDS.yellowMaxPct) && (
                <span style={{ color: "var(--text-tertiary)", marginInlineStart: 6 }}>
                  {t("miniProject.status.writer.threshold.widened", { days: durationDays.toFixed(0) })}
                </span>
              )}
            </div>
          </div>

          {/* Budget Progress (migration 190, Skip 2026-05-28).
              Sits between Schedule and Mission per Skip's locked order.
              Renders even when plannedBudgetAmount is null — the four
              buttons are always selectable; only the auto-suggest chip
              hides when there's not enough math to suggest. */}
          <div className={styles.fieldBlock}>
            <label className={styles.fieldLabel}>
              {t("miniProject.status.writer.field.budgetProgress", {
                defaultValue: "Budget Progress",
              })}
            </label>
            <div className={styles.segGroup} role="radiogroup" aria-label="Budget assessment">
              {BUDGET_STATES.map((s) => {
                // Reuse the Mission-goal active-class palette so the
                // visual ladder reads consistently across surfaces:
                //   under_budget  → ahead    (deep green — "best")
                //   on_track      → on_track (green)
                //   trending_over → behind   (yellow)
                //   over_budget   → blocked  (red)
                const activeClassMap: Record<MiniProjectBudgetAssessment, string> = {
                  under_budget:  styles.segBtnActive_ahead,
                  on_track:      styles.segBtnActive_on_track,
                  trending_over: styles.segBtnActive_behind,
                  over_budget:   styles.segBtnActive_blocked,
                };
                const isActive = budgetAssessment === s.value;
                return (
                  <button
                    key={s.value}
                    type="button"
                    role="radio"
                    aria-checked={isActive}
                    className={`${styles.segBtn} ${isActive ? activeClassMap[s.value] : ""}`}
                    onClick={() => setBudgetAssessment(s.value)}
                  >
                    {t(s.labelKey, {
                      defaultValue:
                        s.value === "under_budget"  ? "Under Budget" :
                        s.value === "on_track"      ? "On Track" :
                        s.value === "trending_over" ? "Trending Over" :
                                                       "Over Budget",
                    })}
                  </button>
                );
              })}
            </div>
            {suggestedBudget && spendPct !== null && (
              <div
                style={{
                  marginBlockStart: 6,
                  fontSize: 12,
                  color: "var(--text-secondary)",
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  flexWrap: "wrap",
                }}
              >
                <span>
                  {t("miniProject.status.writer.budget.suggestedPrefix", {
                    defaultValue: "Suggested:",
                  })}
                </span>
                <span
                  className={`${styles.gapChip} ${
                    suggestedBudget === "under_budget" || suggestedBudget === "on_track"
                      ? styles.gapChip_success
                      : suggestedBudget === "trending_over"
                        ? styles.gapChip_warning
                        : styles.gapChip_error
                  }`}
                >
                  <span className={styles.gapDot} aria-hidden="true" />
                  <span>
                    {t(
                      BUDGET_STATES.find((b) => b.value === suggestedBudget)?.labelKey || "",
                      {
                        defaultValue:
                          suggestedBudget === "under_budget"  ? "Under Budget" :
                          suggestedBudget === "on_track"      ? "On Track" :
                          suggestedBudget === "trending_over" ? "Trending Over" :
                                                                 "Over Budget",
                      },
                    )}
                  </span>
                </span>
                <span style={{ color: "var(--text-tertiary)" }}>
                  {t("miniProject.status.writer.budget.suggestedReason", {
                    pct: spendPct.toFixed(0),
                    spent: (recordedSpend ?? 0).toFixed(0),
                    planned: (plannedBudgetAmount ?? 0).toFixed(0),
                    currency: plannedBudgetCurrency || "",
                    defaultValue: `(${spendPct.toFixed(0)}% of ${plannedBudgetCurrency || ""} ${(plannedBudgetAmount ?? 0).toFixed(0)} spent)`,
                  })}
                </span>
              </div>
            )}
            {!plannedBudgetAmount && (
              <div
                style={{
                  marginBlockStart: 6,
                  fontSize: 11,
                  color: "var(--text-tertiary)",
                  fontStyle: "italic",
                }}
              >
                {t("miniProject.status.writer.budget.noPlanHint", {
                  defaultValue:
                    "Set a planned budget in the General settings to enable the auto-suggest.",
                })}
              </div>
            )}
          </div>

          {/* Goal pickers */}
          {goals.length > 0 && (
            <div className={styles.fieldBlock}>
              <label className={styles.fieldLabel}>
                {t("miniProject.status.writer.field.missionGoals")}
              </label>
              <div className={styles.goalList}>
                {goals.map((g) => {
                  // Skip data-entry for checklist primitives — the
                  // GoalDataEntryTray is hidden for those (the goal
                  // card uses inline checkbox toggles instead). Match
                  // that rule here so the icon doesn't appear for a
                  // tray that wouldn't open meaningfully.
                  const showDataEntry = g.primitive !== "checklist";
                  return (
                    <div key={g.goalGuid} className={styles.goalRow}>
                      <div className={styles.goalText}>
                        <div className={styles.goalName}>{g.nameKey}</div>
                        <div className={styles.goalMeta}>
                          {t("miniProject.status.writer.goal.progress", {
                            current: Number(g.currentValue).toString(),
                            target: g.targetValue ? Number(g.targetValue).toString() : "—",
                            unit: g.unitKey || "",
                          })}
                        </div>
                      </div>
                      <div className={styles.segGroup}>
                        {ASSESSMENT_STATES.map((s) => (
                          <button
                            key={s.value}
                            type="button"
                            className={`${styles.segBtn} ${assessments[g.goalGuid] === s.value ? styles[`segBtnActive_${s.value}`] : ""}`}
                            onClick={() => handleAssessmentPick(g.goalGuid, s.value)}
                          >
                            {t(s.labelKey)}
                          </button>
                        ))}
                      </div>
                      {/* Database icon — opens GoalDataEntryTray for
                          this goal so the Lead can update the actual
                          numeric value without leaving the status
                          modal. 2026-05-27. */}
                      {showDataEntry && (
                        <button
                          type="button"
                          onClick={() => setDataEntryGoalGuid(g.goalGuid)}
                          aria-label={t("miniProject.cards.goal.dataEntryAria")}
                          title={t("miniProject.cards.goal.dataEntryTitle")}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            inlineSize: 26,
                            blockSize: 26,
                            padding: 0,
                            marginInlineStart: 6,
                            border: "0.5px solid var(--border-default)",
                            borderRadius: 4,
                            background: "transparent",
                            color: "var(--text-secondary)",
                            cursor: "pointer",
                            flexShrink: 0,
                          }}
                        >
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 16 16"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.3"
                            aria-hidden="true"
                          >
                            <ellipse cx="8" cy="3.25" rx="5.25" ry="1.75" />
                            <path d="M2.75 3.25v9.5c0 .97 2.35 1.75 5.25 1.75s5.25-.78 5.25-1.75v-9.5" />
                            <path d="M2.75 7c0 .97 2.35 1.75 5.25 1.75s5.25-.78 5.25-1.75" />
                            <path d="M2.75 10.5c0 .97 2.35 1.75 5.25 1.75s5.25-.78 5.25-1.75" />
                          </svg>
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── DEV-1296: Journal / feed posting (Lead-only, write/edit-time) ── */}
          <div className={styles.journalShare}>
            <label className={styles.journalRow}>
              <input
                type="checkbox"
                checked={postToJournal}
                onChange={(e) => setPostToJournal(e.target.checked)}
              />
              <span>{t("miniProject.status.writer.postToJournal")}</span>
            </label>
            {postToJournal && (
              <>
                <input
                  type="text"
                  className={styles.journalHeadline}
                  value={journalHeadline}
                  onChange={(e) => setJournalHeadline(e.target.value)}
                  placeholder={t("miniProject.status.writer.journalHeadlinePlaceholder")}
                />
                <label className={styles.journalRow}>
                  <input
                    type="checkbox"
                    checked={postToFeed}
                    onChange={(e) => setPostToFeed(e.target.checked)}
                  />
                  <span>{t("miniProject.status.writer.postToFeed")}</span>
                </label>
              </>
            )}
          </div>

          {error && <div className={styles.errorMsg}>{error}</div>}
        </div>

        {/* ── Footer ── */}
        <footer className={styles.footer}>
          <button type="button" className={styles.btnCancel} onClick={handleCancel} disabled={saving}>
            {t("miniProject.status.writer.btn.cancel")}
          </button>
          <button type="button" className={styles.btnPost} onClick={handleSave} disabled={saving}>
            {saving
              ? isEdit ? t("miniProject.status.writer.btn.saving") : t("miniProject.status.writer.btn.posting")
              : isEdit ? t("miniProject.status.writer.btn.saveChanges") : t("miniProject.status.writer.btn.postStatus")}
          </button>
        </footer>
      </div>

      {/* In-modal goal data entry tray (2026-05-27). Keyed per goal
          so internal state resets when the Lead switches goals.
          The Tray uses createPortal itself, so it renders to
          document.body and lays over the status modal scrim. On
          successful save, fire onGoalDataChanged so the parent
          refetches goals — the inline meta row above re-renders
          with the new currentValue when goals[] updates. */}
      <GoalDataEntryTray
        key={`status-writer-data-entry-${dataEntryGoalGuid ?? "none"}`}
        open={dataEntryGoal !== null}
        goal={dataEntryGoal ? adaptGoalToView(dataEntryGoal) : null}
        onClose={() => setDataEntryGoalGuid(null)}
        onMutated={() => {
          // Refetch goals via the parent. Do NOT close the status
          // modal — the Lead is still mid-status; they just updated
          // the numeric value and now return to picking chips.
          onGoalDataChanged?.();
          setDataEntryGoalGuid(null);
        }}
      />
    </div>
  );

  return createPortal(modal, document.body);
};

export default StatusWriter;

/* ── ToolbarBtn helper ─────────────────────────────────────────── */

interface ToolbarBtnProps {
  editor: ReturnType<typeof useEditor>;
  ariaLabel: string;
  onClick: () => void;
  active: boolean;
  children: React.ReactNode;
}

function ToolbarBtn({ editor, ariaLabel, onClick, active, children }: ToolbarBtnProps) {
  return (
    <button
      type="button"
      className={`${styles.tbBtn} ${active ? styles.tbBtnActive : ""}`}
      onClick={onClick}
      disabled={!editor}
      aria-label={ariaLabel}
    >
      {children}
    </button>
  );
}
